import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../utils/api';
import { formatDate } from '../utils/styles';

const COLORS = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316','#84cc16'];

function PieSection({ title, sub, data, totalValue }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20 }}>
      <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{title}</div>
      <div style={{ fontSize:12, color:'#9ca3af', marginBottom:14 }}>{sub}</div>
      {data.length === 0 ? <div style={{ textAlign:'center', color:'#9ca3af', padding:30 }}>No data yet</div> : (
        <div style={{ display:'flex', gap:16, alignItems:'center' }}>
          <PieChart width={170} height={170}>
            <Pie data={data} cx={80} cy={80} innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
              {data.map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={v => [`$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2})}`, 'Value']} />
          </PieChart>
          <div style={{ flex:1 }}>
            {data.map((d,i) => {
              const pct = totalValue > 0 ? Math.round((d.value/totalValue)*100) : 0;
              return (
                <div key={d.name} style={{ display:'flex', justifyContent:'space-between', marginBottom:6, alignItems:'center' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:COLORS[i%COLORS.length], flexShrink:0 }} />
                    <span style={{ fontSize:12, fontWeight:500 }}>{d.name}</span>
                    {pct >= 40 && <span style={{ fontSize:10, color:'#ef4444' }}>⚠️</span>}
                  </div>
                  <span style={{ fontSize:12, color:pct>=40?'#ef4444':'#6b7280', fontWeight:pct>=40?600:400 }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const [view, setView] = useState('shipped'); // 'shipped' | 'orders'
  const [monthly, setMonthly] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [settings, setSettings] = useState({ target_monthly_usd: 120000, target_containers: 4, avg_order_value: 30000 });
  const [editTargets, setEditTargets] = useState(false);
  const [targetForm, setTargetForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));

  useEffect(() => {
    Promise.all([
      api.get('/inventory/analytics/monthly'),
      api.get('/inventory/analytics/customers'),
      api.get('/inventory/analytics/pipeline'),
      api.get('/inventory/settings'),
    ]).then(([m,c,p,s]) => {
      setMonthly(m.data);
      setCustomers(c.data);
      setPipeline(p.data);
      setSettings(s.data);
      setTargetForm(s.data);
    }).finally(() => setLoading(false));
  }, []);

  const saveTargets = async () => {
    try {
      await Promise.all(Object.entries(targetForm).map(([k,v]) => api.put(`/inventory/settings/${k}`, { value: v })));
      setSettings(targetForm);
      setEditTargets(false);
    } catch(e) { alert('Error saving targets'); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>Loading analytics...</div>;

  const TARGET = settings.target_monthly_usd || 120000;
  const AVG_ORDER = settings.avg_order_value || 30000;

  // ── SHIPPED VIEW DATA ──
  const yearData = monthly.filter(m => m.month?.startsWith(yearFilter));
  const prevYear = monthly.filter(m => m.month?.startsWith(String(parseInt(yearFilter)-1)));
  const chartData = yearData.map(m => ({
    ...m,
    month_label: new Date(m.month+'-01').toLocaleDateString('en-IN',{month:'short'}),
    prev_year: prevYear.find(p => p.month?.slice(5) === m.month?.slice(5))?.value_usd || 0,
  }));
  const ytdValue = yearData.reduce((s,m) => s+(m.value_usd||0), 0);
  const ytdPcs = yearData.reduce((s,m) => s+(m.total_pcs||0), 0);
  const ytdMonths = yearData.filter(m => m.value_usd > 0).length;
  const avgMonthly = ytdMonths > 0 ? ytdValue/ytdMonths : 0;
  const bestMonth = [...yearData].sort((a,b) => b.value_usd-a.value_usd)[0];
  const totalCustValue = customers.reduce((s,c) => s+(c.value_usd||0), 0);
  const custPieData = customers.filter(c=>c.value_usd>0).sort((a,b)=>b.value_usd-a.value_usd).slice(0,8).map(c=>({ name:c.company_name, value:c.value_usd }));
  const regionMap = {};
  customers.forEach(c => { const r=c.region||'Other'; regionMap[r]=(regionMap[r]||0)+(c.value_usd||0); });
  const regionPieData = Object.entries(regionMap).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([name,value])=>({ name, value }));
  const years = [...new Set(monthly.map(m=>m.month?.split('-')[0]).filter(Boolean))].sort().reverse();

  // ── ORDERS VIEW DATA ──
  const totalActiveValue = pipeline?.activeSummary?.total_value || 0;
  const custPipeData = (pipeline?.custConcentration||[]).map(c => ({ name:c.company_name, value:c.value_usd }));
  const regionPipeData = (pipeline?.regionConc||[]).map(r => ({ name:r.region||'Other', value:r.value_usd }));

  const Card = ({ label, value, sub, color }) => (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 18px', borderLeft:`4px solid ${color}` }}>
      <div style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.6, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:700 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Sales Analytics</h1>
          <div style={{ fontSize:13, color:'#9ca3af', marginTop:3 }}>
            {view === 'shipped' ? 'Dispatch-based — value counted when goods leave factory' : 'Pipeline-based — all active orders in hand'}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {view === 'shipped' && (
            <select value={yearFilter} onChange={e=>setYearFilter(e.target.value)} style={{ padding:'6px 12px', border:'1px solid #e5e7eb', borderRadius:7, fontSize:13 }}>
              {years.map(y=><option key={y}>{y}</option>)}
              {!years.length && <option>{yearFilter}</option>}
            </select>
          )}
          <button onClick={() => setEditTargets(!editTargets)}
            style={{ padding:'7px 14px', border:'1px solid #e5e7eb', borderRadius:7, background:'#fff', fontSize:13, cursor:'pointer' }}>
            ⚙️ Targets
          </button>
        </div>
      </div>

      {/* Toggle */}
      <div style={{ display:'flex', gap:0, marginBottom:20, background:'#f3f4f6', borderRadius:8, padding:3, width:'fit-content' }}>
        {[['shipped','📦 Shipped orders'],['orders','🔮 All orders (pipeline)']].map(([v,l]) => (
          <button key={v} onClick={() => setView(v)} style={{
            padding:'8px 20px', borderRadius:6, border:'none', cursor:'pointer', fontSize:13, fontWeight:500,
            background:view===v?'#fff':'transparent', color:view===v?'#111827':'#6b7280',
            boxShadow:view===v?'0 1px 4px rgba(0,0,0,.1)':'none'
          }}>{l}</button>
        ))}
      </div>

      {/* Targets editor */}
      {editTargets && (
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:18, marginBottom:18 }}>
          <div style={{ fontWeight:600, fontSize:14, marginBottom:14 }}>Edit targets</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:14 }}>
            {[
              ['target_monthly_usd','Monthly dispatch target (USD)','120000'],
              ['target_containers','Containers per month target','4'],
              ['avg_order_value','Average order value (USD) — for planning','30000'],
            ].map(([key,label,placeholder]) => (
              <div key={key}>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:4 }}>{label}</label>
                <input type="number" value={targetForm[key]||''} onChange={e => setTargetForm(p=>({...p,[key]:parseFloat(e.target.value)||0}))}
                  style={{ width:'100%', padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:14 }} placeholder={placeholder} />
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={saveTargets} style={{ padding:'7px 16px', background:'#3b82f6', color:'#fff', border:'none', borderRadius:6, fontSize:13, cursor:'pointer', fontWeight:500 }}>Save targets</button>
            <button onClick={() => { setEditTargets(false); setTargetForm(settings); }} style={{ padding:'7px 14px', background:'#f3f4f6', border:'none', borderRadius:6, fontSize:13, cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ══ SHIPPED ORDERS VIEW ══ */}
      {view === 'shipped' && (
        <>
          {/* YTD cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:18 }}>
            <Card label="YTD dispatched" value={`$${ytdValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} sub={`${yearFilter} so far`} color="#3b82f6" />
            <Card label="Monthly average" value={`$${avgMonthly.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} sub={`vs $${TARGET.toLocaleString()} target`} color={avgMonthly>=TARGET?'#10b981':'#f59e0b'} />
            <Card label="Best month" value={bestMonth?new Date(bestMonth.month+'-01').toLocaleDateString('en-IN',{month:'short'}):'—'} sub={bestMonth?`$${bestMonth.value_usd.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:''} color="#8b5cf6" />
            <Card label="Total pcs shipped" value={ytdPcs.toLocaleString()} sub={`${yearFilter} YTD`} color="#10b981" />
          </div>

          {/* Bar chart */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20, marginBottom:18 }}>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:4 }}>Monthly dispatch value</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:16 }}>{yearFilter} vs {parseInt(yearFilter)-1} — target ${TARGET.toLocaleString()}/month</div>
            {chartData.length===0 ? (
              <div style={{ textAlign:'center', color:'#9ca3af', padding:40 }}>No dispatch data for {yearFilter} yet</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData} margin={{top:5,right:10,bottom:5,left:20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month_label" tick={{fontSize:12}} />
                    <YAxis tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} tick={{fontSize:11}} />
                    <Tooltip formatter={(v,name)=>[`$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2})}`,name==='value_usd'?yearFilter:String(parseInt(yearFilter)-1)]} />
                    <Bar dataKey="value_usd" fill="#3b82f6" name={yearFilter} radius={[3,3,0,0]} />
                    <Bar dataKey="prev_year" fill="#e5e7eb" name={String(parseInt(yearFilter)-1)} radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginTop:10 }}>
                  {chartData.map(m => {
                    const pct = TARGET>0?((m.value_usd||0)/TARGET)*100:0;
                    return (
                      <div key={m.month} style={{ fontSize:11, textAlign:'center', minWidth:52 }}>
                        <div style={{ height:4, width:52, background:'#e5e7eb', borderRadius:2, overflow:'hidden', marginBottom:3 }}>
                          <div style={{ width:`${Math.min(100,pct)}%`, height:'100%', background:pct>=100?'#10b981':pct>=60?'#3b82f6':'#f59e0b' }} />
                        </div>
                        <div style={{ color:'#9ca3af' }}>{m.month_label}</div>
                        <div style={{ fontWeight:500, color:pct>=100?'#10b981':pct>=60?'#3b82f6':'#f59e0b' }}>{pct.toFixed(1)}%</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Concentration pies */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18, marginBottom:18 }}>
            <PieSection title="Customer concentration" sub="Dispatched value by customer" data={custPieData} totalValue={totalCustValue} />
            <PieSection title="Country concentration" sub="Dispatched value by country" data={regionPieData} totalValue={totalCustValue} />
          </div>

          {/* Customer table */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20, marginBottom:18 }}>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:14 }}>Customer breakdown — shipped</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['Customer','Country','Region','Dispatched value','Share'].map(h=>(
                <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.5, background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {customers.filter(c=>c.value_usd>0).sort((a,b)=>b.value_usd-a.value_usd).map(c => {
                  const pct = totalCustValue>0?Math.round((c.value_usd/totalCustValue)*100):0;
                  return (
                    <tr key={c.company_name} style={{ borderBottom:'1px solid #f3f4f6' }}>
                      <td style={{ padding:'10px 12px', fontSize:13, fontWeight:500 }}>{c.company_name}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, color:'#6b7280' }}>{c.country}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, color:'#6b7280' }}>{c.region}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, fontWeight:500, color:'#3b82f6' }}>${(c.value_usd||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td style={{ padding:'10px 12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:60, height:5, background:'#e5e7eb', borderRadius:3, overflow:'hidden' }}>
                            <div style={{ width:`${pct}%`, height:'100%', background:pct>=40?'#ef4444':'#3b82f6' }} />
                          </div>
                          <span style={{ fontSize:12, color:pct>=40?'#ef4444':'#6b7280', fontWeight:pct>=40?600:400 }}>{pct}%{pct>=40?' ⚠️':''}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {customers.filter(c=>c.value_usd>0).length===0 && (
                  <tr><td colSpan={5} style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>No dispatch data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Shipped orders list */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20 }}>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:14 }}>Shipped orders history</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['PI Number','Customer','Dispatch date','Pcs shipped','Value dispatched','Containers'].map(h=>(
                <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.5, background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {(pipeline?.shipped||[]).length===0 ? (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>No orders dispatched yet</td></tr>
                ) : (pipeline?.shipped||[]).map(s => (
                  <tr key={s.pi_number} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ padding:'10px 10px' }}><Link to={`/orders`} style={{ color:'#3b82f6', fontWeight:600, textDecoration:'none', fontSize:13 }}>{s.pi_number}</Link></td>
                    <td style={{ padding:'10px 10px', fontSize:13 }}>{s.company_name}</td>
                    <td style={{ padding:'10px 10px', fontSize:13, fontWeight:500 }}>{formatDate(s.dispatch_date)}</td>
                    <td style={{ padding:'10px 10px', fontSize:13 }}>{(s.pcs_shipped||0).toLocaleString()}</td>
                    <td style={{ padding:'10px 10px', fontSize:13, fontWeight:600, color:'#10b981' }}>${(s.value_shipped||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td style={{ padding:'10px 10px', fontSize:13, color:'#6b7280' }}>{s.containers_count||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ══ ALL ORDERS (PIPELINE) VIEW ══ */}
      {view === 'orders' && (
        <>
          {/* Summary cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:18 }}>
            <Card label="Active orders" value={pipeline?.activeSummary?.count||0} sub="Orders in production" color="#3b82f6" />
            <Card label="Total pipeline value" value={`$${(totalActiveValue||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} sub="All active orders combined" color="#8b5cf6" />
            <Card label="Monthly target" value={`$${(TARGET||0).toLocaleString()}`} sub="Click ⚙️ Targets to edit" color="#f59e0b" />
          </div>

          {/* Month pipeline table */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20, marginBottom:18 }}>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:4 }}>Month-by-month pipeline</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:16 }}>Based on active orders expected ship dates. Target: ${TARGET.toLocaleString()}/month. Avg order: ${AVG_ORDER.toLocaleString()}.</div>
            {(pipeline?.pipeline||[]).length===0 ? (
              <div style={{ textAlign:'center', color:'#9ca3af', padding:40 }}>No future shipments planned. Add expected ship dates to active orders.</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {['Month','Orders','Expected value','Already dispatched','Total vs target','Still needed','Orders needed'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.4, background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(pipeline?.pipeline||[]).map(m => {
                    const pct = TARGET>0?(m.totalExpected/TARGET)*100:0;
                    const monthLabel = new Date(m.month+'-01').toLocaleDateString('en-IN',{month:'long',year:'numeric'});
                    return (
                      <tr key={m.month} style={{ borderBottom:'1px solid #f3f4f6' }}>
                        <td style={{ padding:'12px 10px', fontWeight:600, fontSize:13 }}>{monthLabel}</td>
                        <td style={{ padding:'12px 10px', fontSize:13 }}>
                          <div style={{ fontWeight:500 }}>{m.order_count} order{m.order_count!==1?'s':''}</div>
                          <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>{m.orders_detail}</div>
                        </td>
                        <td style={{ padding:'12px 10px', fontSize:13, fontWeight:500 }}>${(m.expected_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                        <td style={{ padding:'12px 10px', fontSize:13, color:'#10b981' }}>
                          {m.alreadyDispatched>0?`$${m.alreadyDispatched.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:'—'}
                        </td>
                        <td style={{ padding:'12px 10px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:70, height:6, background:'#e5e7eb', borderRadius:3, overflow:'hidden' }}>
                              <div style={{ width:`${Math.min(100,pct)}%`, height:'100%', background:pct>=100?'#10b981':pct>=60?'#3b82f6':'#f59e0b' }} />
                            </div>
                            <span style={{ fontSize:12, fontWeight:600, color:pct>=100?'#10b981':pct>=60?'#3b82f6':'#f59e0b' }}>{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td style={{ padding:'12px 10px', fontSize:13 }}>
                          {m.stillNeeded>0 ? (
                            <span style={{ color:'#ef4444', fontWeight:500 }}>${m.stillNeeded.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                          ) : <span style={{ color:'#10b981', fontWeight:600 }}>✓ Target met</span>}
                        </td>
                        <td style={{ padding:'12px 10px' }}>
                          {m.ordersNeeded>0 ? (
                            <span style={{ background:'#fef2f2', color:'#ef4444', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500 }}>~{m.ordersNeeded} more</span>
                          ) : <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500 }}>✓ On track</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Concentration pies for active orders */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18, marginBottom:18 }}>
            <PieSection title="Customer concentration" sub="All active orders value by customer" data={custPipeData} totalValue={totalActiveValue} />
            <PieSection title="Country concentration" sub="All active orders value by country" data={regionPipeData} totalValue={totalActiveValue} />
          </div>

          {/* Customer breakdown for active orders */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20 }}>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:14 }}>Customer breakdown — active orders</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['Customer','Country','Region','Active orders','Pipeline value','Share'].map(h=>(
                <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.5, background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {(pipeline?.custConcentration||[]).filter(c=>c.value_usd>0).map(c => {
                  const pct = totalActiveValue>0?Math.round((c.value_usd/totalActiveValue)*100):0;
                  return (
                    <tr key={c.company_name} style={{ borderBottom:'1px solid #f3f4f6' }}>
                      <td style={{ padding:'10px 12px', fontSize:13, fontWeight:500 }}>{c.company_name}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, color:'#6b7280' }}>{c.country}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, color:'#6b7280' }}>{c.region}</td>
                      <td style={{ padding:'10px 12px', fontSize:13 }}>{c.order_count}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, fontWeight:500, color:'#8b5cf6' }}>${(c.value_usd||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td style={{ padding:'10px 12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:60, height:5, background:'#e5e7eb', borderRadius:3, overflow:'hidden' }}>
                            <div style={{ width:`${pct}%`, height:'100%', background:pct>=40?'#ef4444':'#8b5cf6' }} />
                          </div>
                          <span style={{ fontSize:12, color:pct>=40?'#ef4444':'#6b7280', fontWeight:pct>=40?600:400 }}>{pct}%{pct>=40?' ⚠️':''}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(pipeline?.custConcentration||[]).filter(c=>c.value_usd>0).length===0 && (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>No active orders</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
